/**
 * Kinetic pipeline API client — review queue and decision trace queries.
 *
 * Requests are routed through the gateway (/api/v1/traces/*) which
 * proxies to the kinetic_pipeline_service. This keeps the frontend
 * talking to a single origin — no extra CORS config needed.
 */

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import type { ReviewTraceResponse, TraceListResponse } from '@/types/api';
import axios from 'axios';

const kineticApi = axios.create({
  baseURL: config.GATEWAY_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

kineticApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

/** Fetch decision traces in the review queue (IN_REVIEW only). */
export async function fetchReviewQueue(
  limit = 20,
  offset = 0,
): Promise<TraceListResponse> {
  const { data } = await kineticApi.get<TraceListResponse>(
    '/api/v1/traces/review-queue',
    { params: { limit, offset } },
  );
  return data;
}

/** Approve or reject an IN_REVIEW trace. */
export async function reviewTrace(
  traceId: string,
  action: 'approve' | 'reject',
  rationale?: string,
): Promise<ReviewTraceResponse> {
  const { data } = await kineticApi.post<ReviewTraceResponse>(
    `/api/v1/traces/${traceId}/review`,
    { action, rationale: rationale ?? null },
  );
  return data;
}
